import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AGE_OF_MAJORITY, BUILDING_DEFS, COUNCIL_SALARY, COUNCIL_SEAT_DEFS, RANK_NAMES, RANK_ORDER, SCHEMES, formatDate,
} from '@pk/shared';
import type {
  BuildingKey, CharacterSummary, CouncilSeat, Id, RealmLaws, TitleSummary, TitleTenureView, TitleView, WarGoalType, WorldView,
} from '@pk/shared';
import { Link } from 'react-router-dom';
import { actorOf, memberIds, useCharacter, useCharacters, useCommand, useHouse, useTitle, useTitleHistory, useTitleIndex, useWorld } from '../api/hooks';
import type { HouseView } from '@pk/shared';
import { Badge, Bar, Btn, Card, Collapsible, ConfirmRow, Empty, ErrorBox, Field, Inline, Loading, PageHeader, Panel, Screen, ScreenTop, ScrollArea, Segmented, Sheet, Tag } from '../components/ui';
import { IconAlert, IconBanner, IconBook, IconCrown, IconDagger, IconHourglass, IconPlus, IconQuill, IconRealm, IconScroll, IconShield, IconUsers, IconWar, ResGold } from '../components/icons';
import '../styles/screens-realm.css';
import { CharLink, CharPortrait, CharacterPicker, HolderLine, TitleLink, canAfford, costText, useEta, useHourEta } from '../components/game';
import { lawRows } from '../lib/laws';
import { formatEta, msForDays } from '../lib/eta';
import { CityEconomy, HoldingEconomy, RealmSizePanel } from './HoldingEconomy';
import { CreateTitlePanel, SettlePanel } from './Frontier';

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V'];
const GOALS: WarGoalType[] = ['seize_title', 'tribute', 'humiliate', 'independence', 'depose'];
const SUCCESSION: RealmLaws['succession'][] = ['primogeniture', 'gavelkind', 'elective', 'seniority'];
const GENDER: RealmLaws['gender'][] = ['male_preference', 'equal', 'female_preference'];
const CROWN: RealmLaws['crownAuthority'][] = [0, 1, 2, 3];
const TAX_CAPS = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6];

type Ctx = { title: TitleView; house: HouseView; world: WorldView; mine: boolean; actor: Id };
type Cmd = ReturnType<typeof useCommand>;

export function TitleScreen() {
  const { id } = useParams();
  const { t } = useTranslation();
  const title = useTitle(id);
  const house = useHouse();
  const world = useWorld();
  const index = useTitleIndex();

  if (title.isLoading || house.isLoading || world.isLoading) return <Loading />;
  if (title.isError || !title.data) return <ErrorBox error={title.error} onRetry={() => title.refetch()} />;
  if (!house.data || !world.data) return <ErrorBox error={house.error ?? world.error} onRetry={() => { void house.refetch(); void world.refetch(); }} />;

  const tt = title.data;
  const h = house.data;
  const mine = !!tt.holderId && memberIds(h).has(tt.holderId);
  const actor = mine ? tt.holderId! : actorOf(h);
  const ctx: Ctx = { title: tt, house: h, world: world.data, mine, actor };
  const liege = tt.liegeTitleId ? index.get(tt.liegeTitleId) : null;
  const dejure = tt.deJureParentId ? index.get(tt.deJureParentId) : null;
  const liegeIsMine = !!liege?.holderHouseId && liege.holderHouseId === h.house.id;
  return <TitleBody key={tt.id} ctx={ctx} liege={liege ?? null} dejure={dejure ?? null} liegeIsMine={liegeIsMine} />;
}

/** Walk a parent link upward through the title index (cycle-safe), root-most title first. */
function chainOf(index: Map<Id, TitleSummary>, start: Id | null, link: 'deJureParentId' | 'liegeTitleId'): TitleSummary[] {
  const out: TitleSummary[] = [];
  const seen = new Set<Id>();
  for (let id = start; id && !seen.has(id); ) {
    seen.add(id);
    const x = index.get(id);
    if (!x) break;
    out.unshift(x);
    id = x[link];
  }
  return out;
}

function CrumbTrail({ items, current }: { items: TitleSummary[]; current: TitleSummary }) {
  return (
    <ol className="crumbs">
      {items.map((x) => (
        <li key={x.id}>
          <Link to={`/title/${x.id}`} className="crumb">
            <span className="crumb-rank">{RANK_NAMES[x.rank].title}</span>
            <span className="crumb-name">{x.name}</span>
          </Link>
        </li>
      ))}
      <li><span className="crumb here" aria-current="page">
        <span className="crumb-rank">{RANK_NAMES[current.rank].title}</span>
        <span className="crumb-name">{current.name}</span>
      </span></li>
    </ol>
  );
}

/** Where this title sits: de jure chain up to the kingdom, plus the de facto liege chain when it differs. */
function TitleHierarchy({ title }: { title: TitleView }) {
  const { t } = useTranslation();
  const index = useTitleIndex();
  const dejure = chainOf(index, title.deJureParentId, 'deJureParentId');
  const defacto = chainOf(index, title.liegeTitleId, 'liegeTitleId');
  const same = dejure.length === defacto.length && dejure.every((x, i) => x.id === defacto[i].id);
  if (dejure.length === 0 && defacto.length === 0) return null;
  return (
    <nav className="title-hier" aria-label={t('title.hierarchy')}>
      <div className="hier-row">
        <span className="hier-label">{t('title.partOf')}</span>
        {dejure.length > 0 ? <CrumbTrail items={dejure} current={title} /> : <span className="muted small">{t('title.topOfRealm')}</span>}
      </div>
      {!same && defacto.length > 0 && (
        <div className="hier-row defacto">
          <span className="hier-label">{t('title.heldUnder')}</span>
          <CrumbTrail items={defacto} current={title} />
        </div>
      )}
    </nav>
  );
}

type TabKey = 'overview' | 'holding' | 'council' | 'laws' | 'history';
type IndexEntry = NonNullable<ReturnType<ReturnType<typeof useTitleIndex>['get']>>;

function TitleBody({ ctx, liege, dejure, liegeIsMine }: { ctx: Ctx; liege: IndexEntry | null; dejure: IndexEntry | null; liegeIsMine: boolean }) {
  const { t } = useTranslation();
  const { title: tt, mine } = ctx;
  const [sp] = useSearchParams();
  const [tab, setTab] = useState<TabKey>(() => (['holding', 'council', 'laws', 'history'] as const).find((k) => k === sp.get('tab')) ?? 'overview');
  const tabs: { value: TabKey; label: string }[] = [{ value: 'overview', label: t('title.tab.overview') }];
  if (tt.holding) tabs.push({ value: 'holding', label: t('title.tab.holding') });
  if (tt.council.length > 0) tabs.push({ value: 'council', label: t('title.tab.council') });
  if (tt.laws || tt.elective) tabs.push({ value: 'laws', label: t('title.tab.laws') });
  tabs.push({ value: 'history', label: t('title.tab.history') });
  const cur: TabKey = tabs.some((x) => x.value === tab) ? tab : 'overview';

  return (
    <Screen className="title-page">
      <ScreenTop>
      <PageHeader
        icon={<IconCrown />}
        title={tt.name}
        subtitle={<span className="title-tags">
          <span className="title-swatch" style={{ background: tt.color }} aria-hidden="true" />
          <span className="title-rank">{RANK_NAMES[tt.rank].title}</span>
          {mine && <Badge tone="green">{t('title.yours')}</Badge>}
          {!tt.holderId && <Badge>{tt.frontier?.uncreated ? t('frontier.uncreated') : tt.frontier?.settled === false ? t('frontier.unsettled') : t('common.vacant')}</Badge>}
          {tt.holding?.occupiedBy && <Badge tone="red">{t('title.occupied')}</Badge>}
        </span>}
      />
      <div className="title-tabs"><Segmented<TabKey> value={cur} onChange={setTab} options={tabs} /></div>
      </ScreenTop>

      <ScrollArea>
      <TitleHierarchy title={tt} />

      <div className="title-facts">
        <div className="fact">
          <span className="fact-label">{t('title.holderLabel')}</span>
          <span className="fact-value"><HolderLine id={tt.holderId} houseId={tt.holderHouseId} name={tt.holderName} /></span>
        </div>
        <div className="fact">
          <span className="fact-label">{t('title.liege')}</span>
          <span className="fact-value">
            {liege ? <><TitleLink id={liege.id} />{liege.holderId && <span className="small muted"><br /><CharLink id={liege.holderId} fallback={liege.holderName ?? undefined} /></span>}</> : <span className="muted">{t('common.none')}</span>}
          </span>
        </div>
        <div className="fact">
          <span className="fact-label">{t('title.heir')}</span>
          <span className="fact-value"><CharLink id={tt.heirId} /></span>
        </div>
        {mine ? (
          <div className="fact">
            <span className="fact-label">{t('title.income')}</span>
            <span className="fact-value num"><ResGold size={18} /> {t('title.incomeValue', { gold: Math.round(tt.monthlyIncome * 10) / 10 })}</span>
          </div>
        ) : tt.holderId ? (
          <div className="fact">
            <span className="fact-label">{t('title.taxDemanded')}</span>
            <span className="fact-value num">{Math.round(tt.vassalTaxRate * 100)}% / {Math.round(tt.vassalLevyRate * 100)}%</span>
          </div>
        ) : null}
      </div>

      <ActionsSection ctx={ctx} liegeIsMine={liegeIsMine} liegeHolderId={liege?.holderId ?? null} goTab={setTab} />
      {tt.frontier?.uncreated && <CreateTitlePanel title={tt} />}
      {tt.frontier && tt.frontier.settled === false && (
        <Panel icon={<IconBanner />} title={t('frontier.siteTitle')}><SettlePanel site={tt} /></Panel>
      )}

      {cur === 'overview' && (
        <>
          <Panel icon={<IconScroll />} title={t('title.overview')}>
            <div className="kv"><span>{t('title.deJure')}</span>{dejure ? <TitleLink id={dejure.id} /> : <span className="muted">{t('common.none')}</span>}</div>
            {dejure && liege && dejure.id !== liege.id && <div className="kv"><span>{t('title.liegeDeFacto')}</span><TitleLink id={liege.id} /></div>}
            {tt.capitalId && tt.capitalId !== tt.id && <div className="kv"><span>{t('title.capital')}</span><TitleLink id={tt.capitalId} /></div>}
            {tt.vassalTitleIds.length === 0 && <div className="kv"><span>{t('title.rates.tax')} / {t('title.rates.levy')}</span><span className="num">{Math.round(tt.vassalTaxRate * 100)}% / {Math.round(tt.vassalLevyRate * 100)}%</span></div>}
          </Panel>
          {mine && tt.realmSize && <RealmSizePanel title={tt} />}
          {(tt.knightLimit > 0 || tt.knightIds.length > 0 || tt.guardLimit > 0 || tt.claimantIds.length > 0) && (
            <Panel icon={<IconUsers />} title={t('title.people')}>
              {(tt.knightLimit > 0 || tt.knightIds.length > 0) && (
                <div className="kv">
                  <span>{t('title.knights')} <span className="muted small num">{tt.knightIds.length} / {tt.knightLimit}</span></span>
                  <span style={{ textAlign: 'right' }}>{tt.knightIds.length === 0 ? <span className="muted">{t('common.none')}</span> : tt.knightIds.map((k, i) => <span key={k}>{i > 0 && ', '}<CharLink id={k} /></span>)}</span>
                </div>
              )}
              {tt.guardLimit > 0 && (
                <>
                  <div className="kv">
                    <span>{t('title.captain')}</span>
                    {tt.guardCaptainId ? <CharLink id={tt.guardCaptainId} /> : <span className="muted">{t('title.noCaptain')}</span>}
                  </div>
                  <div className="kv">
                    <span>{t('title.guard')} <span className="muted small num">{tt.guardIds.length} / {tt.guardLimit}</span></span>
                    <span style={{ textAlign: 'right' }}>{tt.guardIds.length === 0 ? <span className="muted">{t('title.noGuard')}</span> : tt.guardIds.map((g, i) => <span key={g}>{i > 0 && ', '}<CharLink id={g} /></span>)}</span>
                  </div>
                </>
              )}
              {tt.claimantIds.length > 0 && <div className="kv"><span>{t('title.claimants')}</span><span style={{ textAlign: 'right' }}>{tt.claimantIds.map((k, i) => <span key={k}>{i > 0 && ', '}<CharLink id={k} /></span>)}</span></div>}
            </Panel>
          )}
          {tt.vassalTitleIds.length > 0 && <VassalSection ctx={ctx} />}
        </>
      )}
      {cur === 'holding' && tt.holding && <HoldingSection ctx={ctx} />}
      {cur === 'council' && <CouncilSection ctx={ctx} />}
      {cur === 'laws' && (
        <>
          {tt.elective && <ElectionSection ctx={ctx} />}
          {tt.laws && <LawsSection ctx={ctx} laws={tt.laws} />}
        </>
      )}
      {cur === 'history' && <TitleHistorySection titleId={tt.id} />}
      </ScrollArea>
    </Screen>
  );
}

// ------------------------------------------------------------------ holding & buildings

/** Tenure ledger rows (newest first), shared by the Title and Character history sections. */
export function TenureRows({ tenures }: { tenures: TitleTenureView[] }) {
  const { t } = useTranslation();
  return (
    <>
      {tenures.map((ten) => (
        <div key={ten.holderId + ten.startDay} className="kv">
          <span>
            <CharLink id={ten.holderId} fallback={ten.holderName ?? undefined} />
            {ten.houseId && <span className="muted"> · <Link className="link" to={`/tree/${ten.houseId}`}>{ten.houseName}</Link></span>}
          </span>
          <span style={{ textAlign: 'right' }}>
            <span className="num">
              {ten.endDay === null
                ? t('history.since', { date: formatDate(ten.startDay) })
                : `${formatDate(ten.startDay)} – ${formatDate(ten.endDay)}`}
              {ten.endDay === null && <Tag tone="gold">{t('history.current')}</Tag>}
            </span>
            <span className="small muted" style={{ display: 'block' }}>{t(`history.reason.${ten.reason}`)}</span>
          </span>
        </div>
      ))}
    </>
  );
}

function TitleHistorySection({ titleId }: { titleId: Id }) {
  const { t } = useTranslation();
  const history = useTitleHistory(titleId);
  return (
    <Panel icon={<IconBook />} title={t('history.section')}>
      {history.isLoading && <Loading />}
      {history.isError && <ErrorBox error={history.error} onRetry={() => history.refetch()} />}
      {history.data && (history.data.length === 0
        ? <Empty>{t('common.none')}</Empty>
        : <TenureRows tenures={history.data} />)}
    </Panel>
  );
}

// ------------------------------------------------------------------ holding & buildings (original)

function HoldingSection({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { title, house, world, mine, actor } = ctx;
  const hd = title.holding!;
  const cmd = useCommand();
  const eta = useEta();
  const hourEta = useHourEta();
  const res = house.house.resources;
  const keys = Object.keys(BUILDING_DEFS) as BuildingKey[];
  const rows = keys.filter((k) => {
    const def = BUILDING_DEFS[k];
    const lvl = hd.buildings[k] ?? 0;
    const allowed = def.allowedTerrain === 'any' || def.allowedTerrain.includes(hd.terrain);
    return lvl > 0 || hd.construction?.building === k || (mine && allowed);
  });

  const renderBuilding = (k: BuildingKey) => {
        const def = BUILDING_DEFS[k];
        const lvl = hd.buildings[k] ?? 0;
        const target = lvl + 1;
        const building = hd.construction?.building === k;
        const maxed = lvl >= def.maxLevel;
        const allowed = def.allowedTerrain === 'any' || def.allowedTerrain.includes(hd.terrain);
        const busy = !!hd.construction;
        const afford = canAfford(res, def.cost, target);
        const noSlot = lvl === 0 && !!title.economy && title.economy.slots.used >= title.economy.slots.total;
        let reason: string | null = null;
        if (!allowed) reason = t('title.build.terrain');
        else if (maxed) reason = t('title.build.maxed');
        else if (noSlot) reason = t('title.build.noSlot');
        else if (busy) reason = t('title.build.busy');
        else if (!afford) reason = t('title.build.afford');
        return (
          <div key={k} className="bld">
            <div className="row between">
              <h3>{def.name} {lvl > 0 && <span className="muted">{ROMAN[lvl]}</span>}</h3>
              {building && <Tag tone="blue">{t('title.building')}</Tag>}
            </div>
            <p className="small muted" style={{ margin: '2px 0 8px' }}>{def.description}</p>
            {building && hd.construction && (
              <p className="small">{t('title.constructing', {
                level: ROMAN[hd.construction.level],
                date: hd.construction.doneHour != null ? hourEta(hd.construction.doneHour).when : formatDate(hd.construction.doneDay),
                eta: hd.construction.doneHour != null ? hourEta(hd.construction.doneHour).eta : eta(hd.construction.doneDay),
              })}</p>
            )}
            {mine && !maxed && allowed && (
              <>
                <p className="small">{t('title.build.cost', { level: ROMAN[target], cost: costText(def.cost, target, t as never), days: def.days * 1, eta: formatEta(msForDays(world, def.days), t as never) })}</p>
                <Btn small variant="ghost" disabled={!!reason} busy={cmd.pending} title={reason ?? undefined}
                  onClick={() => void cmd.run({ type: 'build', actorId: actor, holdingId: title.id, building: k })}>
                  {lvl === 0 ? t('title.build.start') : t('title.build.upgrade', { level: ROMAN[target] })}
                </Btn>
                {reason && <span className="small muted" style={{ marginLeft: 8 }}>{reason}</span>}
              </>
            )}
          </div>
        );
  };
  const active = rows.filter((k) => (hd.buildings[k] ?? 0) > 0 || hd.construction?.building === k);
  const others = rows.filter((k) => !active.includes(k));

  return (
    <>
    <Panel icon={<IconShield />} title={t('title.holding')}>
      {title.economy ? <HoldingEconomy title={title} house={house} world={world} mine={mine} actor={actor} /> : (
      <div className="attrs">
        <Bar label={t('title.development')} value={hd.development} max={10} />
      </div>)}
      <div className="attrs">
        <div className="kv"><span>{t('title.terrain')}</span><span>{t(`terrain.${hd.terrain}`)}</span></div>
        <div className="kv"><span>{t('title.garrison')}</span><span className="num">{hd.garrison}</span></div>
        <div className="kv"><span>{t('title.levyPool')}</span><span className="num">{hd.levyPool}</span></div>
      </div>

      <h3 className="subhead">{t('title.buildings')}</h3>
      {rows.length === 0 && <Empty>{t('title.noBuildings')}</Empty>}
      {active.map(renderBuilding)}
      {others.length > 0 && (
        <Collapsible title={t('ui.availableBuildings')} summary={others.length} className="flush">
          {others.map(renderBuilding)}
        </Collapsible>
      )}
      <Inline error={cmd.error} />
    </Panel>
    {title.city && <CityEconomy title={title} house={house} world={world} mine={mine} actor={actor} />}
    </>
  );
}

// ------------------------------------------------------------------ council

function CouncilSection({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { title, mine } = ctx;
  const [seat, setSeat] = useState<CouncilSeat | null>(null);
  if (title.council.length === 0) return null;
  return (
    <Panel icon={<IconQuill />} title={t('title.council')}>
      {mine && <p className="small muted">{t('title.councilSalary', { gold: COUNCIL_SALARY[title.rank] })}</p>}
      {title.council.map((c) => {
        const def = COUNCIL_SEAT_DEFS[c.seat];
        return (
          <Card key={c.seat}>
            <div className="row between">
              <div className="grow">
                <div className="rankname">{def.name}</div>
                <div className="small muted">{def.description}</div>
                <div style={{ marginTop: 4 }}>{c.charId ? <CharLink id={c.charId} fallback={c.charName ?? undefined} /> : <span className="muted">{t('common.vacant')}</span>}</div>
              </div>
              {mine && <Btn small onClick={() => setSeat(c.seat)}>{c.charId ? t('title.councilSeat.change') : t('title.councilSeat.appoint')}</Btn>}
            </div>
          </Card>
        );
      })}
      <Sheet open={seat !== null} onClose={() => setSeat(null)} title={seat ? t('title.councilSeat.sheet', { seat: COUNCIL_SEAT_DEFS[seat].name }) : ''}>
        {seat && <AppointForm ctx={ctx} seat={seat} onDone={() => setSeat(null)} />}
      </Sheet>
    </Panel>
  );
}

function AppointForm({ ctx, seat, onDone }: { ctx: Ctx; seat: CouncilSeat; onDone: () => void }) {
  const { t } = useTranslation();
  const { title, house, actor } = ctx;
  const cmd = useCommand();
  const attr = COUNCIL_SEAT_DEFS[seat].attribute;
  const current = title.council.find((c) => c.seat === seat)?.charId ?? null;
  // Council rule: only direct subjects of the holder who sit on no council yet (the server lists them).
  const candIds = [...(current ? [current] : []), ...(title.councilCandidateIds ?? []).filter((id) => id !== current)];
  const cands = useCharacters(candIds).map((q) => q.data).filter((x): x is NonNullable<typeof x> => !!x && x.alive);
  const [sel, setSel] = useState<Id | null>(current);
  const changed = sel !== current;
  const apply = async (charId: Id | null) => { const r = await cmd.run({ type: 'appoint_council', actorId: actor, titleId: title.id, seat, charId }); if (r.ok) onDone(); };
  return (
    <div>
      <p className="muted">{t('title.councilSeat.help', { attr: t(`attr.${attr}`) })}</p>
      <ul className="picklist plainlist">
        {cands.map((c) => (
          <li key={c.id}>
            <button type="button" className={`pick ${sel === c.id ? 'on' : ''}`} onClick={() => setSel(c.id)}>
              <CharPortrait c={c} size={30} />
              <span>{c.firstName}</span>
              <span className="muted small">{t(`attr.${attr}`)} {c.attributes[attr as keyof typeof c.attributes]}</span>
            </button>
          </li>
        ))}
        {cands.length === 0 && <li className="empty">{title.councilClosedReason ?? t('title.councilSeat.noCandidates')}</li>}
      </ul>
      <p className="small muted" style={{ margin: '10px 0' }}>{title.councilClosedReason ?? t('title.councilSeat.rule')}</p>
      <Inline error={cmd.error} />
      <div className="row" style={{ marginTop: 10 }}>
        <Btn variant="primary" disabled={!sel || !changed} busy={cmd.pending} onClick={() => void apply(sel)}>{t('title.councilSeat.appoint')}</Btn>
        {current && <Btn variant="danger" busy={cmd.pending} onClick={() => void apply(null)}>{t('title.councilSeat.clear')}</Btn>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ vassals

function VassalSection({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { title, mine } = ctx;
  const index = useTitleIndex();
  const [open, setOpen] = useState(false);
  return (
    <Panel icon={<IconRealm />} title={t('title.vassals')} right={mine && <Btn small onClick={() => setOpen(true)}>{t('title.rates.set')}</Btn>}>
      <div className="kv"><span>{t('title.rates.tax')}</span><span className="num">{Math.round(title.vassalTaxRate * 100)}%</span></div>
      <div className="kv"><span>{t('title.rates.levy')}</span><span className="num">{Math.round(title.vassalLevyRate * 100)}%</span></div>
      {title.vassalTitleIds.map((vid) => {
        const v = index.get(vid);
        return (
          <div key={vid} className="kv">
            <TitleLink id={vid} />
            <span style={{ textAlign: 'right' }}>{v?.holderId ? <HolderLine id={v.holderId} houseId={v.holderHouseId} name={v.holderName} /> : <span className="muted">{t('common.vacant')}</span>}</span>
          </div>
        );
      })}
      <Sheet open={open} onClose={() => setOpen(false)} title={t('title.rates.title', { name: title.name })}>
        <RatesForm ctx={ctx} onDone={() => setOpen(false)} />
      </Sheet>
    </Panel>
  );
}

function RatesForm({ ctx, onDone }: { ctx: Ctx; onDone: () => void }) {
  const { t } = useTranslation();
  const { title, actor, world } = ctx;
  // Tax cap comes from the kingdom this title ultimately belongs to (walk de jure parents).
  const realmId = useMemo(() => {
    const byId = new Map(world.titles.map((x) => [x.id, x]));
    let cur: TitleSummary | undefined = title;
    let guard = 0;
    while (cur && guard++ < 8) {
      if (cur.rank === 'kingdom') return cur.id;
      cur = cur.deJureParentId ? byId.get(cur.deJureParentId) : undefined;
    }
    return null;
  }, [title, world.titles]);
  const kingdom = useTitle(realmId ?? undefined);
  const cap = kingdom.data?.laws?.taxCap ?? null;
  const cmd = useCommand();
  const [tax, setTax] = useState(Math.min(title.vassalTaxRate, cap ?? 1));
  const [levy, setLevy] = useState(title.vassalLevyRate);
  return (
    <div>
      {cap !== null && (
        <>
          <p className="muted">{t('title.rates.help', { cap: Math.round(cap * 100) })}</p>
          <Field label={t('title.rates.taxValue', { pct: Math.round(tax * 100) })}>
            <input type="range" min={0} max={cap} step={0.05} value={tax} onChange={(e) => setTax(Math.min(cap, Number(e.target.value)))} />
          </Field>
        </>
      )}
      <Field label={t('title.rates.levyValue', { pct: Math.round(levy * 100) })} hint={t('title.rates.levyHint')}>
        <input type="range" min={0} max={1} step={0.05} value={levy} onChange={(e) => setLevy(Number(e.target.value))} />
      </Field>
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="primary" block busy={cmd.pending}
          onClick={async () => { const r = await cmd.run({ type: 'set_vassal_rates', actorId: actor, titleId: title.id, taxRate: Math.round(tax * 100) / 100, levyRate: Math.round(levy * 100) / 100 }); if (r.ok) onDone(); }}>
          {t('common.save')}
        </Btn>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ laws

function LawsSection({ ctx, laws }: { ctx: Ctx; laws: RealmLaws }) {
  const { t } = useTranslation();
  const { title, mine, actor } = ctx;
  const cmd = useCommand();
  const [draft, setDraft] = useState<Partial<Record<keyof RealmLaws, string>>>({});
  const rows = lawRows(laws, t as never);

  const optionsFor = (k: keyof RealmLaws): { value: string; label: string }[] => {
    switch (k) {
      case 'succession': return SUCCESSION.map((v) => ({ value: v, label: t(`laws.succession_.${v}`) }));
      case 'gender': return GENDER.map((v) => ({ value: v, label: t(`laws.gender_.${v}`) }));
      case 'crownAuthority': return CROWN.map((v) => ({ value: String(v), label: t(`laws.crown_.${v}`) }));
      case 'taxCap': {
        const set = new Set([...TAX_CAPS, laws.taxCap]);
        return [...set].sort((a, b) => a - b).map((v) => ({ value: String(v), label: `${Math.round(v * 100)}%` }));
      }
      default: return [{ value: 'false', label: t('laws.allowed') }, { value: 'true', label: t('laws.banned') }];
    }
  };
  const parse = (k: keyof RealmLaws, v: string): RealmLaws[keyof RealmLaws] => {
    if (k === 'crownAuthority') return Number(v) as RealmLaws['crownAuthority'];
    if (k === 'taxCap') return Number(v);
    if (k === 'internalWarsBanned') return v === 'true';
    return v as RealmLaws[keyof RealmLaws];
  };

  return (
    <Panel icon={<IconScroll />} title={t('title.lawsHeading')}>
      {rows.map((r) => {
        const cur = String(laws[r.key]);
        const sel = draft[r.key] ?? cur;
        return (
          <div key={r.key} className="lawrow">
            <div className="kv" style={{ borderBottom: 0 }}><span>{r.label}</span>{!mine && <span>{r.value}</span>}</div>
            {mine && (
              <div className="row" style={{ flexWrap: 'nowrap' }}>
                <select className="input grow" value={sel} aria-label={r.label} onChange={(e) => setDraft({ ...draft, [r.key]: e.target.value })}>
                  {optionsFor(r.key).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {sel !== cur && (
                  <Btn small variant="primary" busy={cmd.pending}
                    onClick={async () => {
                      const res = await cmd.run({ type: 'set_law', actorId: actor, titleId: title.id, law: r.key, value: parse(r.key, sel) });
                      if (res.ok) setDraft((d) => { const n = { ...d }; delete n[r.key]; return n; });
                    }}>{t('title.laws.propose')}</Btn>
                )}
              </div>
            )}
          </div>
        );
      })}
      {mine ? <p className="small muted">{t('title.laws.help')}</p> : <p className="small muted">{t('title.laws.readonly')}</p>}
      <Inline error={cmd.error} />
    </Panel>
  );
}

// ------------------------------------------------------------------ elective succession

function ElectionSection({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { title, house } = ctx;
  const el = title.elective!;
  const cmd = useCommand();
  // Any living member of the player's house who is an elector may cast the vote — not just the head.
  const electorId = useMemo(
    () => house.members.find((m) => m.alive && el.electorIds.includes(m.id))?.id ?? null,
    [house.members, el.electorIds],
  );
  const [choice, setChoice] = useState<Id | null>(null);
  const counted = new Map<Id, number>();
  for (const c of Object.values(el.votes)) counted.set(c, (counted.get(c) ?? 0) + 1);
  // A stale local choice (route/title/candidate set changed) must never be submitted:
  // fall back to the current valid server-side vote, then to null.
  const serverVote = electorId ? el.votes[electorId] ?? null : null;
  const selected = choice !== null && el.candidateIds.includes(choice) ? choice : serverVote;

  return (
    <Panel icon={<IconCrown />} title={t('title.election.title')}>
      <div className="kv"><span>{t('title.election.heir')}</span><CharLink id={title.heirId} /></div>
      {electorId ? (
        <div role="radiogroup" aria-label={t('title.election.title')}>
          <p className="small muted">{t('title.election.help')}</p>
          {el.candidateIds.map((cid) => (
            <label key={cid} className="kv election-row">
              <span className="row" style={{ flexWrap: 'nowrap' }}>
                <input type="radio" name={`elective-${title.id}`} value={cid}
                  checked={selected === cid} onChange={() => setChoice(cid)}
                  aria-label={t('title.election.vote')} />
                <CharLink id={cid} />
              </span>
              <span className="num small">{t('title.election.votes', { count: counted.get(cid) ?? 0 })}</span>
            </label>
          ))}
          <Inline error={cmd.error} />
          <Btn variant="primary" disabled={!selected} busy={cmd.pending}
            onClick={() => {
              // After a successful command the refreshed server data is authoritative — drop the local choice.
              void cmd.run({ type: 'cast_elective_vote', actorId: electorId, titleId: title.id, candidateId: selected! })
                .then((r) => { if (r.ok) setChoice(null); });
            }}>
            {t('title.election.vote')}
          </Btn>
        </div>
      ) : (
        <p className="small muted">{t('title.election.noElector')}</p>
      )}
    </Panel>
  );
}

// ------------------------------------------------------------------ grant / revoke / war

function ActionsSection({ ctx, liegeIsMine, liegeHolderId, goTab }: { ctx: Ctx; liegeIsMine: boolean; liegeHolderId: Id | null; goTab: (tab: TabKey) => void }) {
  const { t } = useTranslation();
  const { title, house, mine } = ctx;
  const [dlg, setDlg] = useState<null | 'grant' | 'revoke' | 'war'>(null);
  const cmd = useCommand();
  const holder = useCharacter(mine ? title.holderId : null);
  const index = useTitleIndex();
  const close = () => { setDlg(null); cmd.clearError(); };

  // A holder's primary title is their highest-ranking one; it cannot be granted away.
  const held = (holder.data?.heldTitleIds ?? []).map((x) => index.get(x)).filter((x): x is NonNullable<typeof x> => !!x);
  const primary = held.reduce<(typeof held)[number] | null>((best, x) => (!best || RANK_ORDER[x.rank] > RANK_ORDER[best.rank] ? x : best), null);
  const isPrimary = primary?.id === title.id;
  const canGrant = mine && !isPrimary && !!holder.data;
  const canRevoke = liegeIsMine && !mine && !!title.holderId;
  const canWar = !!title.holderId && !mine && !memberIds(house).has(title.holderId);
  const canFabricate = !mine && !!title.holderId && !title.claimantIds.includes(ctx.actor);
  const scheming = house.schemes.length > 0;

  const el = title.elective;
  const canVote = !!el && house.members.some((m) => m.alive && el.electorIds.includes(m.id));
  const manage: { key: string; label: string; icon: ReactNode; tab: TabKey }[] = [];
  if (mine && title.holding) manage.push({ key: 'build', label: t('title.go.build'), icon: <IconShield />, tab: 'holding' });
  if (mine && title.council.length > 0) manage.push({ key: 'council', label: t('title.go.council'), icon: <IconQuill />, tab: 'council' });
  if (mine && title.vassalTitleIds.length > 0) manage.push({ key: 'rates', label: t('title.go.rates'), icon: <IconRealm />, tab: 'overview' });
  if (mine && title.laws) manage.push({ key: 'laws', label: t('title.go.laws'), icon: <IconScroll />, tab: 'laws' });
  if (canVote) manage.push({ key: 'vote', label: t('title.go.vote'), icon: <IconCrown />, tab: 'laws' });
  const nothing = !canGrant && !canRevoke && !canWar && !canFabricate && manage.length === 0;

  return (
    <Panel tone="gold" icon={<IconDagger />} title={t('title.actions')} className="title-actions">
      {nothing && <p className="small muted">{t('title.noActions')}</p>}
      {(canGrant || canRevoke || canWar || canFabricate) && (
        <div className="act-grid">
          {canGrant && <Btn icon={<IconPlus />} onClick={() => setDlg('grant')}>{t('title.act.grant')}</Btn>}
          {canRevoke && <Btn variant="danger" icon={<IconAlert />} onClick={() => setDlg('revoke')}>{t('title.act.revoke')}</Btn>}
          {canWar && <Btn variant="danger" icon={<IconWar />} onClick={() => setDlg('war')}>{t('title.act.war', { name: title.holderName ?? '' })}</Btn>}
          {canFabricate && (
            <Btn variant="danger" icon={<IconDagger />} busy={cmd.pending} disabled={scheming} title={scheming ? t('scheme.already') : undefined}
              onClick={() => { if (!title.holderId) return; void cmd.run({ type: 'start_scheme', actorId: ctx.actor, targetId: title.holderId, schemeType: 'fabricate_claim', targetTitleId: title.id }); }}>
              {t('scheme.fabricate', { cost: SCHEMES.fabricate_claim.cost })}
            </Btn>
          )}
        </div>
      )}
      {manage.length > 0 && (
        <div className="act-grid">
          {manage.map((m) => <Btn key={m.key} small variant="quiet" icon={m.icon} onClick={() => goTab(m.tab)}>{m.label}</Btn>)}
        </div>
      )}
      {canFabricate && scheming && <p className="small muted act-note"><IconHourglass />{t('scheme.already')}</p>}
      {mine && isPrimary && <p className="small muted act-note"><IconShield />{t('title.grant.primary')}</p>}
      <Inline error={cmd.error} />

      <Sheet open={dlg === 'grant'} onClose={close} title={t('title.grant.title', { name: title.name })}>
        <GrantForm ctx={ctx} cmd={cmd} onDone={close} />
      </Sheet>
      <Sheet open={dlg === 'revoke'} onClose={close} title={t('title.revoke.title', { name: title.name })}>
        <ConfirmRow message={t('title.revoke.confirm', { name: title.holderName ?? '', title: title.name })} busy={cmd.pending}
          onConfirm={async () => { const r = await cmd.run({ type: 'revoke_title', actorId: liegeHolderId ?? ctx.actor, titleId: title.id }); if (r.ok) close(); }} onCancel={close} />
        <Inline error={cmd.error} />
      </Sheet>
      <Sheet open={dlg === 'war'} onClose={close} title={t('war.declare.title', { name: title.holderName ?? '' })}>
        {dlg === 'war' && <DeclareWarForm ctx={ctx} cmd={cmd} onDone={close} />}
      </Sheet>
    </Panel>
  );
}

function GrantForm({ ctx, cmd, onDone }: { ctx: Ctx; cmd: Cmd; onDone: () => void }) {
  const { t } = useTranslation();
  const { title, house, actor } = ctx;
  const [to, setTo] = useState<Id | null>(null);
  const [other, setOther] = useState(false);
  const adults = house.members.filter((m) => m.alive && m.age >= AGE_OF_MAJORITY && m.id !== title.holderId);
  return (
    <div>
      <p className="muted">{t('title.grant.help')}</p>
      <ul className="picklist plainlist">
        {adults.map((m) => (
          <li key={m.id}>
            <button type="button" className={`pick ${to === m.id ? 'on' : ''}`} onClick={() => setTo(m.id)}>
              <CharPortrait c={m} size={30} /><span>{m.firstName}</span>
              <span className="muted small">{t('common.age', { age: m.age })}{m.primaryTitle ? ` · ${m.primaryTitle}` : ''}</span>
            </button>
          </li>
        ))}
        {adults.length === 0 && <li className="empty">{t('title.grant.none')}</li>}
      </ul>
      <button type="button" className="link" style={{ margin: '10px 0' }} onClick={() => setOther(!other)}>{t('title.grant.other')}</button>
      {other && <CharacterPicker value={to} onChange={(id) => setTo(id)} filter={(c: CharacterSummary) => c.age >= AGE_OF_MAJORITY} />}
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="primary" block disabled={!to} busy={cmd.pending} onClick={async () => { const r = await cmd.run({ type: 'grant_title', actorId: actor, titleId: title.id, toCharId: to! }); if (r.ok) onDone(); }}>
          {t('title.grant.submit')}
        </Btn>
      </div>
    </div>
  );
}

function DeclareWarForm({ ctx, cmd, onDone }: { ctx: Ctx; cmd: Cmd; onDone: () => void }) {
  const { t } = useTranslation();
  const { title, house } = ctx;
  const actor = actorOf(house);
  const index = useTitleIndex();
  const target = useCharacter(title.holderId);
  const [goal, setGoal] = useState<WarGoalType>('seize_title');
  const needsTitle = goal === 'seize_title' || goal === 'depose' || goal === 'independence';
  const options = useMemo(() => {
    const ids = goal === 'independence' ? house.heldTitleIds : (target.data?.heldTitleIds ?? [title.id]);
    return ids.map((x) => index.get(x)).filter((x): x is NonNullable<typeof x> => !!x);
  }, [goal, house.heldTitleIds, target.data, title.id, index]);
  const [picked, setPicked] = useState<Id | ''>('');
  const titleId = options.some((o) => o.id === picked) ? picked : (options.find((o) => o.id === title.id)?.id ?? options[0]?.id ?? '');
  const hasClaim = goal === 'seize_title' && house.members.some((m) => m.claimTitleIds.includes(titleId));

  return (
    <div>
      <p className="muted">{t('war.declare.help')}</p>
      <Field label={t('war.declare.goal')}>
        <select className="input" value={goal} onChange={(e) => setGoal(e.target.value as WarGoalType)}>
          {GOALS.map((g) => <option key={g} value={g}>{t(`war.goal.${g}`)}</option>)}
        </select>
      </Field>
      <p className="small muted">{t(`war.goalHelp.${goal}`)}</p>
      {needsTitle && (
        <Field label={goal === 'independence' ? t('war.declare.freeFrom') : t('war.declare.target')}>
          <select className="input" value={titleId} onChange={(e) => setPicked(e.target.value)}>
            {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Field>
      )}
      {goal === 'seize_title' && (hasClaim ? <Tag tone="green">{t('war.declare.claim')}</Tag> : <p className="small muted">{t('war.declare.noClaim')}</p>)}
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="danger" block disabled={needsTitle && !titleId} busy={cmd.pending}
          onClick={async () => { const r = await cmd.run({ type: 'declare_war', actorId: actor, targetCharId: title.holderId!, goal, titleId: needsTitle ? titleId : null }); if (r.ok) onDone(); }}>
          {t('war.declare.submit')}
        </Btn>
      </div>
    </div>
  );
}
